import { useEffect, useState } from 'react'
import AppCombobox from '../../../../components/AppCombobox'
import { fetchProjectOptions } from '../../../../api/filterOptionApi/projectFilterApi'
import { fetchTaskOptions } from '../../../../api/filterOptionApi/taskFilterApi'

const EMPTY_FORM = {
  hours: '0',
  minutes: '0',
  projectId: '',
  projectLabel: '',
  taskId: '',
  taskLabel: '',
  error: '',
}

export default function ProductivityAdjustmentEditModal({ item, isSaving = false, onClose, onSubmit }) {
  const [form, setForm] = useState(EMPTY_FORM)

  useEffect(() => {
    if (!item) {
      setForm(EMPTY_FORM)
      return
    }
    const seconds = Math.max(0, Number(item.manualActiveSeconds || 0))
    setForm({
      hours: String(Math.floor(seconds / 3600)),
      minutes: String(Math.floor((seconds % 3600) / 60)),
      projectId: item.projectId ? String(item.projectId) : '',
      projectLabel: item.projectName ? `${item.projectName}${item.projectCode ? ` (${item.projectCode})` : ''}` : '',
      taskId: item.taskId ? String(item.taskId) : '',
      taskLabel: item.taskName ? `${item.taskName}${item.taskCode ? ` (${item.taskCode})` : ''}` : '',
      error: '',
    })
  }, [item])

  if (!item) return null

  const fetchMappedTasks = (search, page, pageSize) => (
    fetchTaskOptions(search, page, pageSize, form.projectId || null)
  )

  async function handleSubmit() {
    const hours = Number(form.hours || 0)
    const minutes = Number(form.minutes || 0)
    const safeHours = Number.isFinite(hours) ? Math.max(0, Math.floor(hours)) : 0
    const safeMinutes = Number.isFinite(minutes) ? Math.max(0, Math.min(59, Math.floor(minutes))) : 0
    const manualActiveSeconds = (safeHours * 3600) + (safeMinutes * 60)
    if (manualActiveSeconds <= 0) {
      setForm((previous) => ({ ...previous, error: 'Requested time must be greater than 0 minutes.' }))
      return
    }
    await onSubmit?.({
      manualActiveSeconds,
      projectId: form.projectId ? Number(form.projectId) : null,
      taskId: form.taskId ? Number(form.taskId) : null,
    })
  }

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/35 p-4">
      <div className="w-full max-w-lg rounded-2xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-2xl">
        <h2 className="text-xl font-bold text-on-surface">Edit Productivity Request</h2>
        <p className="mt-2 text-sm text-on-surface-variant">
          Update the Project, mapped Task, and requested productive time while this approval is pending.
        </p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Hours</span>
            <input type="number" min="0" value={form.hours} onChange={(event) => setForm((previous) => ({ ...previous, hours: event.target.value, error: '' }))} className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none" />
          </label>
          <label className="block">
            <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Minutes</span>
            <input type="number" min="0" max="59" value={form.minutes} onChange={(event) => setForm((previous) => ({ ...previous, minutes: event.target.value, error: '' }))} className="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none" />
          </label>
        </div>
        <label className="mt-4 block">
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Project</span>
          <AppCombobox
            value={form.projectId}
            initialLabel={form.projectLabel}
            onChange={(value) => setForm((previous) => ({ ...previous, projectId: value, projectLabel: '', taskId: '', taskLabel: '', error: '' }))}
            placeholder="Select Project..."
            searchPlaceholder="Search project..."
            fetchOptions={fetchProjectOptions}
            triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
          />
        </label>
        <label className="mt-4 block">
          <span className="mb-2 block text-xs font-bold uppercase tracking-wider text-on-surface-variant">Linked Task (In-Progress / Draft)</span>
          <AppCombobox
            value={form.taskId}
            initialLabel={form.taskLabel}
            onChange={(value) => setForm((previous) => ({ ...previous, taskId: value, taskLabel: '', error: '' }))}
            placeholder="Select Task..."
            searchPlaceholder="Search task..."
            fetchOptions={fetchMappedTasks}
            disabled={!form.projectId}
            triggerClassName="w-full rounded-md border-none bg-surface-container-low px-4 py-3 text-sm text-on-surface outline-none"
          />
        </label>
        {form.error ? <div className="mt-3 rounded-md border border-error/30 bg-error-container px-3 py-2 text-xs text-error">{form.error}</div> : null}
        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="rounded-md bg-secondary-container px-5 py-2 text-sm font-bold text-on-secondary-container">Cancel</button>
          <button type="button" onClick={handleSubmit} disabled={isSaving} className="rounded-md bg-primary px-5 py-2 text-sm font-bold text-on-primary disabled:opacity-60">{isSaving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}
